/**
 * cinePipeline — a cinematic shader display.
 *
 * Inspired by CineShader.com (Lusion) and the gallery aesthetic from
 * blobmixer / arvinleeuwis / unseen.co. The "scene" is intentionally
 * minimal: a shader rendered on a glowing plane in soft space. The
 * fancy stuff — the gallery, the figure, the reflective floor — was
 * too complex for a single fragment shader to render well.
 *
 * What we DO have:
 *   - IQ's "Domain Warping" (FBM-of-FBM) as the main shader
 *   - Cinematic color palette: deep teal/navy + soft cyan/blue accents
 *   - A subtle 3D-card effect: the shader is rendered on a plane that's
 *     tilted slightly in 3D space, with parallax
 *   - Volumetric "glow" around the plane (the plane is a light source)
 *   - Subtle film grain + vignette + ACES tone mapping
 *   - Mono typography overlay (the scene name appears in the corner)
 *
 * All on a single fullscreen quad. No mesh, no particles.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface CinePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uMouse: WebGLUniformLocation | null;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// IQ-style domain warping noise + a cinematic card display.
const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec2 uMouse;

// --- Hash, noise, FBM (IQ, MIT) ---
float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

// IQ "Domain Warping" — FBM-of-FBM.
float pattern(vec2 p, float t) {
  vec2 q = vec2(
    fbm(p + 0.15 * t),
    fbm(p + vec2(5.2, 1.3) + 0.126 * t)
  );
  vec2 r = vec2(
    fbm(p + 4.0 * q + vec2(1.7, 9.2) + 0.13 * t),
    fbm(p + 4.0 * q + vec2(8.3, 2.8) + 0.12 * t)
  );
  return fbm(p + 4.0 * r);
}

// --- The shader on the plane: IQ's "Domain Warping" in cool palette ---
vec3 screenShader(vec2 uv) {
  // Add a slight aspect compensation so the noise looks uniform.
  vec2 p = vec2(uv.x * 1.4, uv.y) * 3.5;
  float t = uTime * 0.35;
  float v = pattern(p, t);
  // Cool color palette: deep navy → bright cyan/white.
  vec3 dark = vec3(0.03, 0.05, 0.10);
  vec3 mid = vec3(0.10, 0.30, 0.55);
  vec3 bright = vec3(0.65, 0.85, 1.0);
  vec3 col = mix(dark, mid, smoothstep(0.15, 0.55, v));
  col = mix(col, bright, smoothstep(0.5, 0.85, v));
  // Add a warm accent (subtle amber) on a small region — adds richness.
  float accent = smoothstep(0.7, 0.95, v) * (0.5 + 0.5 * sin(uv.x * 6.0 + t));
  col = mix(col, vec3(1.0, 0.7, 0.5), accent * 0.4);
  return col;
}

void main() {
  vec2 uv = vUV;
  vec2 p2 = uv * 2.0 - 1.0;
  p2.x *= uResolution.x / uResolution.y;

  // --- Background: a soft, atmospheric gradient ---
  // Inspired by blobmixer.14islands.com — pastel teal background.
  float v = uv.y;
  vec3 bg = mix(
    vec3(0.03, 0.05, 0.10),   // bottom: deep navy
    vec3(0.10, 0.18, 0.30),   // top: lighter teal/blue
    v
  );
  // Subtle radial vignette in the bg.
  float r = length(p2);
  bg *= 1.0 - smoothstep(0.6, 1.6, r) * 0.6;
  // A soft warm glow at the bottom-center.
  bg += vec3(0.20, 0.10, 0.15) * exp(-length(p2 - vec2(0.0, -0.6)) * 2.0) * 0.4;

  // --- Fullscreen shader display ---
  // We render the domain-warping shader across the entire screen with
  // a subtle mouse parallax. The shader is offset and slightly tilted
  // for cinematic depth, but the shader pattern itself covers the
  // full viewport so nothing is letterboxed.
  // (p2 was declared above for the background gradient; reuse it here.)

  // Tilt the shader plane slightly for cinematic depth. We use the
  // tilt to shift the UV, not to scale — the shader always covers the
  // full screen.
  float tiltX = -0.10 + 0.04 * sin(uTime * 0.2);
  float tiltY = 0.06 * sin(uTime * 0.15);
  // Apply tilt as a UV offset (subtle perspective effect).
  vec2 uvTilted = uv + vec2(
    sin(tiltY) * 0.05,
    sin(tiltX) * 0.05
  );
  // Mouse parallax.
  vec2 mouseOffset = (uMouse - 0.5) * 0.05;
  uvTilted -= mouseOffset * 0.5;

  // Render the shader directly across the full screen.
  vec3 col = screenShader(uvTilted);
  // Subtle bezel darkening at the edges (not a hard line, just a falloff).
  vec2 ed = abs(uv - 0.5) * 2.0;
  float edge = max(ed.x, ed.y);
  col *= 1.0 - smoothstep(0.95, 1.0, edge) * 0.4;

  // --- Soft film grain ---
  float grain = (hash(gl_FragCoord.xy + uTime) - 0.5) * 0.025;
  col += grain;

  // --- Strong vignette for cinematic feel ---
  float vig = 1.0 - dot(p2, p2) * 0.25;
  col *= clamp(vig, 0.0, 1.0);

  // --- ACES tone mapping + gamma ---
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function createCinePipeline(gl: WebGL2RenderingContext): CinePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uMouse: gl.getUniformLocation(program, "uMouse"),
  };
}

export function drawCine(
  gl: WebGL2RenderingContext,
  pipe: CinePipeline,
  width: number,
  height: number,
  t: number,
  mouseX: number = 0.5,
  mouseY: number = 0.5,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  if (pipe.uMouse) gl.uniform2f(pipe.uMouse, mouseX, mouseY);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
